/** @format */

import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { produce } from "immer";
import { api } from "../api/client";
import {
  ProcessingStatusEnum,
  SOP,
  type SOPVersion,
  type TitlePageContent,
  type TitlePageMaterial,
  type RevisionRow,
} from "../types/sop";
import {
  isVersionLockedError,
  getVersionLockedMessage,
  isPermissionDeniedError,
  PERMISSION_DENIED_MESSAGE,
} from "../api/sopErrors";
import * as sopVersionsApi from "../api/sopVersions";
import type { RawVersion } from "../api/sopVersions";
import type { VersionListItem } from "../types/sop";
import { useSopSelectionStore } from "./useSopSelectionStore";

const inFlightSopByIdRequests = new Map<string, Promise<void>>();

/** Raw response from GET /sops/:id (single version, no versions array). Optional ?version_id= for specific version. */
interface GetSOPByIdResponse {
  sopId: string;
  sopTitle?: string;
  createdAt: string;
  processingStatus?: ProcessingStatusEnum;
  processingTimeSeconds?: number;
  titlePage?: TitlePageContent;
  bom?: TitlePageMaterial[];
  operationCount?: number;
  images?: unknown[];
  version: RawVersion;
  orgId?: string;
  organizationId?: string;
  organization_id?: string;
}

export interface SopPagination {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
}

/** Normalize raw API version (snake_case) to SOPVersion. Used by fetchSOPById and completeVersion. */
function normalizeVersionFromRaw(v: RawVersion): SOPVersion {
  const versionId = v.versionId ?? v.version_id ?? "";
  const versionNum = v.versionNum ?? v.version_num ?? 0;
  const materials = v.titlePage?.materials ?? v.materials;
  const rawTitlePage = v.titlePage as
    | (TitlePageContent & { cover_image?: string })
    | undefined;
  const coverImage = rawTitlePage?.coverImage ?? rawTitlePage?.cover_image;
  const titlePage = v.titlePage
    ? {
        ...v.titlePage,
        materials: materials ?? v.titlePage.materials,
        ...(coverImage != null ? { coverImage } : {}),
      }
    : materials?.length
      ? { details: "", materials }
      : undefined;
  return {
    ...v,
    versionId,
    versionNum,
    sopDescription: v.sopDescription ?? v.sop_description,
    status: v.status,
    revisions: v.revisions ?? undefined,
    titlePage: titlePage ?? v.titlePage,
  };
}

/** Optional filters for SOP list and pending-verification endpoints. */
export interface SopListFilters {
  dateFrom?: string;
  dateTo?: string;
  sopTitle?: string;
  organizationId?: string;
  creatorEmail?: string;
}

interface SopActions {
  fetchSOPs: (options?: {
    page?: number;
    limit?: number;
  } & SopListFilters) => Promise<void>;
  fetchSOPById: (
    sopId: string,
    versionId?: string,
    options?: { refreshVersionList?: boolean },
  ) => Promise<void>;
  fetchPendingVerificationSOPs: (options?: {
    page?: number;
    limit?: number;
  } & SopListFilters) => Promise<void>;
  completeVersion: (
    sopId: string,
    versionId: string,
    body: { sop_description: string },
  ) => Promise<{ completed: SOPVersion; newDraftVersion?: SOPVersion }>;
  /**
   * POST /sops/:sopId/versions – create a new draft (e.g. legacy SOPs with only done versions).
   * Returns the new draft; store adds it to the SOP and sets selection to it.
   */
  createNewDraft: (sopId: string) => Promise<{ newDraftVersion: SOPVersion }>;
  addOrUpdateSop: (sop: SOP) => void;
  updateSopTitleById: (sopId: string, newTitle: string) => Promise<void>;
  updateSopTitlePage: (
    sopId: string,
    titlePage: TitlePageContent,
  ) => Promise<void>;
  updateSopRevisions: (
    sopId: string,
    revisions: RevisionRow[],
    versionId?: string,
  ) => Promise<void>;
  updateSopBom: (sopId: string, bom: TitlePageMaterial[]) => Promise<void>;
  addVersionToSOP: (sopId: string, version: SOPVersion, newSop?: true) => void;
  updateSopProcessingStatus: (
    sopId: string,
    status: ProcessingStatusEnum,
    processingTimeSeconds?: number,
  ) => void;
  clearError: () => void;

  // Selectors
  getSopById: (sopId: string) => SOP | null;
  getLatestVersion: (sopId: string) => SOPVersion | null;
  listVersions: (
    sopId: string,
  ) => Promise<import("../types/sop").VersionListItem[]>;
  getVersionList: (sopId: string) => VersionListItem[];
}

interface SopState extends SopActions {
  sops: SOP[];
  pagination: SopPagination | null;
  versionListsBySopId: Record<string, VersionListItem[]>;
  isLoading: boolean;
  error: string | null;
}

export const useSopStore = create<SopState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      sops: [],
      pagination: null,
      versionListsBySopId: {},
      isLoading: false,
      error: null,

      clearError: () => set({ error: null }),

      /**
       * Update the processing status of a SOP
       * @param sopId - The ID of the SOP to update
       * @param status - The new processing status
       * @param processingTimeSeconds - The processing time in seconds (optional)
       */
      updateSopProcessingStatus: (
        sopId: string,
        status: ProcessingStatusEnum,
        processingTimeSeconds?: number,
      ) => {
        set((state) => ({
          sops: state.sops.map((sop) => {
            if (sop.sopId !== sopId) return sop;
            return new SOP(
              sop.sopId,
              sop.sopTitle,
              sop.versions,
              sop.createdAt,
              status,
              processingTimeSeconds,
              sop.bom,
              sop.revisions,
              sop.organizationId,
              sop.creatorEmail,
              sop.organizationName,
            );
          }),
        }));
      },

      /**
       * Fetch paginated SOPs from the backend (list view: flat items with operationCount, no versions).
       */
      fetchSOPs: async (
        options?: { page?: number; limit?: number } & SopListFilters,
      ) => {
        try {
          if (get().isLoading) return;
          set({ isLoading: true, error: null });

          const page = options?.page ?? 1;
          const limit = Math.min(options?.limit ?? 20, 100);
          const params: Record<string, string | number> = {
            page,
            limit,
          };
          const dateFrom = options?.dateFrom?.trim();
          const dateTo = options?.dateTo?.trim();
          const sopTitle = options?.sopTitle?.trim();
          const organizationId = options?.organizationId?.trim();
          const creatorEmail = options?.creatorEmail?.trim();
          if (dateFrom) params.dateFrom = dateFrom;
          if (dateTo) params.dateTo = dateTo;
          if (sopTitle) params.sopTitle = sopTitle;
          if (organizationId) params.organizationId = organizationId;
          if (creatorEmail) params.creatorEmail = creatorEmail;

          const response = await api.get(`/sops`, { params });
          const data = response.data;
          type ListSop = {
            sopId: string;
            sopTitle: string;
            createdAt: string;
            processingStatus?: ProcessingStatusEnum;
            processingTimeSeconds?: number;
            operationCount?: number;
            orgId?: string;
            organizationId?: string;
            organization_id?: string;
            creatorEmail?: string | null;
            organizationName?: string | null;
          };
          const sops = (data?.sops || []).map((sop: ListSop) => {
            const syntheticVersion: SOPVersion = {
              versionId: sop.sopId,
              versionNum: 1,
              versionTitle: sop.sopTitle,
              steps: [],
              editedAt: sop.createdAt,
              operationCount: sop.operationCount ?? 0,
            };
            const orgId =
              sop.orgId ?? sop.organizationId ?? sop.organization_id;
            return new SOP(
              sop.sopId,
              sop.sopTitle,
              [syntheticVersion],
              sop.createdAt,
              sop.processingStatus,
              sop.processingTimeSeconds,
              undefined,
              [],
              orgId,
              sop.creatorEmail ?? undefined,
              sop.organizationName ?? undefined,
            );
          });
          const pag = data?.pagination;
          const pagination: SopPagination | null =
            pag && typeof pag.page === "number" && typeof pag.total === "number"
              ? {
                  page: Number(pag.page),
                  limit: Number(pag.limit ?? limit),
                  total: Number(pag.total),
                  hasNext: Boolean(pag.hasNext),
                }
              : null;
          set({ sops, pagination, isLoading: false });
        } catch (err: unknown) {
          const message =
            err &&
            typeof err === "object" &&
            "response" in err &&
            err.response &&
            typeof (err.response as { data?: { message?: string } }).data
              ?.message === "string"
              ? (err.response as { data: { message: string } }).data.message
              : err instanceof Error
                ? err.message
                : "Failed to fetch SOPs";
          set({ error: message, isLoading: false });
        }
      },

      /**
       * Fetch SOP by ID (single version). No versionId = latest; versionId = that version.
       * Populates version list for dropdown via listVersions.
       */
      fetchSOPById: async (
        sopId: string,
        versionId?: string,
        options?: { refreshVersionList?: boolean },
      ) => {
        const requestKey = `${sopId}:${versionId ?? "__latest__"}:${options?.refreshVersionList === false ? "keep_list" : "refresh_list"}`;
        const existingRequest = inFlightSopByIdRequests.get(requestKey);
        if (existingRequest) {
          return existingRequest;
        }

        set({ error: null });
        const requestPromise = (async () => {
          try {
            const params = versionId ? { version_id: versionId } : undefined;
            const response = await api.get<GetSOPByIdResponse>(`/sops/${sopId}`, {
              params,
            });
            const raw = response.data;
            if (!raw?.version) {
              throw new Error("Invalid response: missing version");
            }
            const normalizedVersion = normalizeVersionFromRaw(raw.version);
            const revisionsFromVersion =
              normalizedVersion.revisions != null &&
              normalizedVersion.revisions.length > 0
                ? normalizedVersion.revisions
                : [];
            const orgId = raw.orgId ?? raw.organizationId ?? raw.organization_id;
            const sop = new SOP(
              raw.sopId,
              raw.sopTitle ?? normalizedVersion.versionTitle,
              [normalizedVersion],
              raw.createdAt,
              raw.processingStatus,
              raw.processingTimeSeconds,
              raw.bom,
              revisionsFromVersion,
              orgId,
              (raw as { creatorEmail?: string | null }).creatorEmail,
              (raw as { organizationName?: string | null }).organizationName,
            );
            get().addOrUpdateSop(sop);
            const refreshVersionList = options?.refreshVersionList !== false;
            if (refreshVersionList) {
              const versionList = await sopVersionsApi.listVersions(sopId);
              set((state) => ({
                versionListsBySopId: {
                  ...state.versionListsBySopId,
                  [sopId]: versionList,
                },
              }));
            } else {
              const hasCachedList =
                (get().versionListsBySopId[sopId]?.length ?? 0) > 0;
              if (!hasCachedList) {
                const versionList = await sopVersionsApi.listVersions(sopId);
                set((state) => ({
                  versionListsBySopId: {
                    ...state.versionListsBySopId,
                    [sopId]: versionList,
                  },
                }));
              }
            }
          } catch (error) {
            const message = isPermissionDeniedError(error)
              ? PERMISSION_DENIED_MESSAGE
              : error instanceof Error
                ? error.message
                : "Failed to fetch SOP";
            set({ error: message });
            throw error;
          } finally {
            inFlightSopByIdRequests.delete(requestKey);
          }
        })();

        inFlightSopByIdRequests.set(requestKey, requestPromise);
        return requestPromise;
      },

      /**
       * Complete the given version (PATCH status "done"). When it is the latest,
       * backend creates the next draft and returns it in newDraftVersion.
       * Updates store and selection to the new draft when present.
       */
      completeVersion: async (
        sopId: string,
        versionId: string,
        body: { sop_description: string },
      ) => {
        const response = await sopVersionsApi.completeVersion(sopId, versionId, {
          status: "done",
          sop_description: body.sop_description,
        });
        const completed = normalizeVersionFromRaw(response);
        const newDraftVersion = response.newDraftVersion
          ? normalizeVersionFromRaw(response.newDraftVersion)
          : undefined;

        const sop = get().getSopById(sopId);
        if (!sop) {
          return { completed, newDraftVersion };
        }

        const newVersions = sop.versions
          .map((v) => (v.versionId === versionId ? completed : v))
          .concat(newDraftVersion ? [newDraftVersion] : [])
          .sort((a, b) => a.versionNum - b.versionNum);

        const updatedSop = new SOP(
          sop.sopId,
          sop.sopTitle,
          newVersions,
          sop.createdAt,
          sop.processingStatus,
          sop.processingTimeSeconds,
          sop.bom,
          sop.revisions,
          sop.organizationId,
          sop.creatorEmail,
          sop.organizationName,
        );
        get().addOrUpdateSop(updatedSop);

        if (newDraftVersion) {
          useSopSelectionStore.setState({
            selectedVersion: newDraftVersion,
            latestVersion: newDraftVersion,
          });
        }

        return { completed, newDraftVersion };
      },

      /**
       * Create a new draft version (POST). For legacy SOPs that only have done/archived versions.
       * Adds the new draft to the SOP and sets selection to it; refreshes version list.
       */
      createNewDraft: async (sopId: string) => {
        const response = await sopVersionsApi.createNewDraft(sopId);
        const newDraftVersion = normalizeVersionFromRaw(
          response.newDraftVersion,
        );
        const sop = get().getSopById(sopId);
        if (sop) {
          const newVersions = [...sop.versions, newDraftVersion].sort(
            (a, b) => a.versionNum - b.versionNum,
          );
          const updatedSop = new SOP(
            sop.sopId,
            sop.sopTitle,
            newVersions,
            sop.createdAt,
            sop.processingStatus,
            sop.processingTimeSeconds,
            sop.bom,
            sop.revisions,
            sop.organizationId,
            sop.creatorEmail,
            sop.organizationName,
          );
          get().addOrUpdateSop(updatedSop);
        } else {
          get().addOrUpdateSop(
            new SOP(
              sopId,
              "",
              [newDraftVersion],
              new Date().toISOString(),
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
            ),
          );
        }
        useSopSelectionStore.setState({
          selectedVersion: newDraftVersion,
          latestVersion: newDraftVersion,
        });
        const versionList = await sopVersionsApi.listVersions(sopId);
        set((state) => ({
          versionListsBySopId: {
            ...state.versionListsBySopId,
            [sopId]: versionList,
          },
        }));
        return { newDraftVersion };
      },

      /**
       * Fetch SOPs with pending_verification status from the backend
       */
      fetchPendingVerificationSOPs: async (
        options?: { page?: number; limit?: number } & SopListFilters,
      ) => {
        try {
          if (get().isLoading) return;
          set({ isLoading: true, error: null });

          const page = options?.page ?? 1;
          const limit = Math.min(options?.limit ?? 20, 100);
          const params: Record<string, string | number> = { page, limit };
          const dateFrom = options?.dateFrom?.trim();
          const dateTo = options?.dateTo?.trim();
          const sopTitle = options?.sopTitle?.trim();
          const organizationId = options?.organizationId?.trim();
          const creatorEmail = options?.creatorEmail?.trim();
          if (dateFrom) params.dateFrom = dateFrom;
          if (dateTo) params.dateTo = dateTo;
          if (sopTitle) params.sopTitle = sopTitle;
          if (organizationId) params.organizationId = organizationId;
          if (creatorEmail) params.creatorEmail = creatorEmail;

          const response = await api.get("/sops/pending-verification", {
            params: Object.keys(params).length ? params : undefined,
          });
          const data = response.data;
          type PendingSop = {
            sopId: string;
            sopTitle: string;
            createdAt: string;
            processingStatus?: ProcessingStatusEnum;
            processingTimeSeconds?: number;
            operationCount?: number;
            orgId?: string;
            organizationId?: string;
            organization_id?: string;
            creatorEmail?: string | null;
            organizationName?: string | null;
          };
          const sops = (data?.sops || []).map(
            (sop: PendingSop) => {
              const syntheticVersion: SOPVersion = {
                versionId: sop.sopId,
                versionNum: 1,
                versionTitle: sop.sopTitle,
                steps: [],
                editedAt: sop.createdAt,
                operationCount: sop.operationCount ?? 0,
              };
              const orgId =
                sop.orgId ?? sop.organizationId ?? sop.organization_id;
              return new SOP(
                sop.sopId,
                sop.sopTitle,
                [syntheticVersion],
                sop.createdAt,
                sop.processingStatus,
                sop.processingTimeSeconds,
                undefined,
                undefined,
                orgId,
                sop.creatorEmail ?? undefined,
                sop.organizationName ?? undefined,
              );
            },
          );
          const pag = data?.pagination;
          const pagination: SopPagination =
            pag && typeof pag.page === "number" && typeof pag.total === "number"
              ? {
                  page: Number(pag.page),
                  limit: Number(pag.limit ?? limit),
                  total: Number(pag.total),
                  hasNext: Boolean(pag.hasNext),
                }
              : {
                  page,
                  limit,
                  total: sops.length,
                  hasNext: false,
                };
          set({ sops, pagination, isLoading: false });
        } catch (err: unknown) {
          const message =
            err &&
            typeof err === "object" &&
            "response" in err &&
            err.response &&
            typeof (err.response as { data?: { message?: string } }).data
              ?.message === "string"
              ? (err.response as { data: { message: string } }).data.message
              : err instanceof Error
                ? err.message
                : "Failed to fetch pending verification SOPs";
          set({ error: message, isLoading: false, pagination: null });
        }
      },

      /**
       * Add or update a single SOP in the store (e.g. for navigation from pending list)
       */
      addOrUpdateSop: (sop: SOP) => {
        set((state) => {
          const existing = state.sops.find((s) => s.sopId === sop.sopId);
          if (existing) {
            return {
              sops: state.sops.map((s) => (s.sopId === sop.sopId ? sop : s)),
            };
          }
          return { sops: [...state.sops, sop] };
        });
      },

      /**
       * Update the title of a SOP
       * @param sopId - The ID of the SOP to update
       * @param newTitle - The new title for the SOP
       * @returns void
       */
      updateSopTitleById: async (sopId: string, newTitle: string) => {
        const existing = get().getSopById(sopId);
        const prevSopTitle = existing?.sopTitle;
        const prevLatestVersionId = existing?.getLatestVersion()?.versionId;
        const prevLatestVersionTitle =
          existing?.getLatestVersion()?.versionTitle;

        try {
          set({ isLoading: true, error: null });

          // Update both SOP title and latest version title in one operation
          set((state) => ({
            sops: state.sops.map((sop) => {
              if (sop.sopId !== sopId) return sop;

              const latestVersion = sop.getLatestVersion();
              const updatedVersions = latestVersion
                ? sop.versions.map((version) =>
                    version.versionId === latestVersion.versionId
                      ? { ...version, versionTitle: newTitle }
                      : version,
                  )
                : sop.versions;

                return new SOP(
                  sopId,
                  newTitle,
                  updatedVersions,
                  sop.createdAt,
                  sop?.processingStatus,
                  sop?.processingTimeSeconds,
                  sop.bom,
                  sop.revisions,
                  sop.organizationId,
                  sop.creatorEmail,
                  sop.organizationName,
                );
            }),
          }));

          await api.put(`/update_sop_title/${sopId}`, { title: newTitle });
          set({ isLoading: false });
        } catch (error) {
          // Revert optimistic update on failure without refetching
          // (refetching can overwrite unsaved edits in the editor).
          if (prevSopTitle && prevLatestVersionId) {
            set((state) => ({
              sops: state.sops.map((sop) => {
                if (sop.sopId !== sopId) return sop;

                const updatedVersions = sop.versions.map((version) =>
                  version.versionId === prevLatestVersionId
                    ? {
                        ...version,
                        versionTitle:
                          prevLatestVersionTitle ?? version.versionTitle,
                      }
                    : version,
                );

                return new SOP(
                  sopId,
                  prevSopTitle,
                  updatedVersions,
                  sop.createdAt,
                  sop?.processingStatus,
                  sop?.processingTimeSeconds,
                  sop.bom,
                  sop.revisions,
                  sop.organizationId,
                  sop.creatorEmail,
                  sop.organizationName,
                );
              }),
            }));
          }
          set({
            error: isVersionLockedError(error)
              ? getVersionLockedMessage(error)
              : error instanceof Error
                ? error.message
                : "Failed to update SOP title",
            isLoading: false,
          });
        }
      },

      /**
       * Update the title page content of a SOP (creates new version)
       * @param sopId - The ID of the SOP to update
       * @param titlePage - The title page content to save
       * @returns void
       */
      updateSopTitlePage: async (
        sopId: string,
        titlePage: TitlePageContent,
      ) => {
        try {
          set({ isLoading: true, error: null });

          const payload = { titlePage };
          await api.put(`/update_sop_title_page/${sopId}`, payload);

          // Update state from successful response so UI shows saved content without refetch or redirect
          set((state) => ({
            sops: state.sops.map((sop) => {
              if (sop.sopId !== sopId) return sop;
              const latest = sop.getLatestVersion();
              if (!latest) return sop;
              const updatedVersion: SOPVersion = {
                ...latest,
                titlePage: { ...titlePage },
              };
              const newVersions = sop.versions.map((v) =>
                v.versionId === latest.versionId ? updatedVersion : v,
              );
              return new SOP(
                sop.sopId,
                sop.sopTitle,
                newVersions,
                sop.createdAt,
                sop.processingStatus,
                sop.processingTimeSeconds,
                sop.bom,
                sop.revisions,
                sop.organizationId,
                sop.creatorEmail,
                sop.organizationName,
              );
            }),
          }));

          set({ isLoading: false });
        } catch (error) {
          set({
            error: isVersionLockedError(error)
              ? getVersionLockedMessage(error)
              : error instanceof Error
                ? error.message
                : "Failed to update SOP title page",
            isLoading: false,
          });
        }
      },

      /**
       * Update the revision history of the latest (draft) version.
       * Calls PUT /update_sop_title with body { revisions, version_id? }.
       * Optional version_id: when provided, response.revisions is for that version.
       */
      updateSopRevisions: async (
        sopId: string,
        revisions: RevisionRow[],
        versionId?: string,
      ) => {
        try {
          set({ isLoading: true, error: null });
          const body: { revisions: RevisionRow[]; version_id?: string } = {
            revisions,
          };
          if (versionId) body.version_id = versionId;
          const response = await api.put(`/update_sop_title/${sopId}`, body);
          const savedRevisions: RevisionRow[] =
            response.data?.revisions ?? revisions;
          set((state) => ({
            sops: state.sops.map((sop) => {
              if (sop.sopId !== sopId) return sop;
              const latest = sop.getLatestVersion();
              const updatedVersions = latest
                ? sop.versions.map((v) =>
                    v.versionId === latest.versionId
                      ? { ...v, revisions: savedRevisions }
                      : v,
                  )
                : sop.versions;
                return new SOP(
                  sop.sopId,
                  sop.sopTitle,
                  updatedVersions,
                  sop.createdAt,
                  sop?.processingStatus,
                  sop?.processingTimeSeconds,
                  sop.bom,
                  savedRevisions,
                  sop.organizationId,
                  sop.creatorEmail,
                  sop.organizationName,
                );
            }),
          }));
          set({ isLoading: false });
        } catch (error) {
          set({
            error: isVersionLockedError(error)
              ? getVersionLockedMessage(error)
              : error instanceof Error
                ? error.message
                : "Failed to update revision history",
            isLoading: false,
          });
        }
      },

      updateSopBom: async (sopId: string, bom: TitlePageMaterial[]) => {
        try {
          set({ isLoading: true, error: null });
          await api.put(`/update_sop_bom/${sopId}`, { bom });
          set((state) => ({
            sops: state.sops.map((sop) =>
              sop.sopId !== sopId
                ? sop
                : new SOP(
                    sop.sopId,
                    sop.sopTitle,
                    sop.versions,
                    sop.createdAt,
                    sop?.processingStatus,
                    sop?.processingTimeSeconds,
                    bom,
                    sop.revisions,
                    sop.organizationId,
                    sop.creatorEmail,
                    sop.organizationName,
                  ),
            ),
          }));
          set({ isLoading: false });
        } catch (error) {
          set({
            error: isVersionLockedError(error)
              ? getVersionLockedMessage(error)
              : error instanceof Error
                ? error.message
                : "Failed to update materials (BOM)",
            isLoading: false,
          });
        }
      },

      addVersionToSOP: (sopId: string, version: SOPVersion, newSop?: true) => {
        if (newSop) {
          set(
            produce((state: SopState) => {
              state.sops.push(
                new SOP(
                  sopId,
                  sopId,
                  [version],
                  version.editedAt,
                  undefined,
                  undefined,
                  undefined,
                  [],
                  undefined,
                  undefined,
                  undefined,
                ),
              );
            }),
          );
        } else {
          set(
            produce((state: SopState) => {
              const sop = state.sops.find((s) => s.sopId === sopId);
              if (sop) {
                const idx = sop.versions.findIndex(
                  (v) => v.versionId === version.versionId,
                );
                if (idx >= 0) {
                  sop.versions[idx] = version;
                } else {
                  sop.versions.push(version);
                }
              }
            }),
          );
        }
      },

      // Selectors
      getSopById: (sopId: string) => {
        const { sops } = get();
        return sops.find((sop) => sop.sopId === sopId) || null;
      },

      getLatestVersion: (sopId: string) => {
        const sop = get().getSopById(sopId);
        return sop?.getLatestVersion() || null;
      },

      listVersions: (sopId: string) => sopVersionsApi.listVersions(sopId),

      getVersionList: (sopId: string) => {
        return get().versionListsBySopId[sopId] ?? [];
      },
    })),
    {
      name: "sop-store",
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);
